/**
 * Case / outer helper — fills the case row's size from items-per-case.
 * Labels follow the case row's base (ml, g or each). Writes case_qty (and
 * unit_size_ml for volume) so the reviewed items-per-case persists; for the
 * count pattern it also keeps the product case_size equal to case_qty.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { measureTypeOf } from './measureType';

const CASE_TYPES = ['case', 'outer', 'keg'];

function findCaseIdx(units) {
  const i = units.findIndex(u => u.is_default_purchase_unit && CASE_TYPES.includes(u.unit_type));
  return i !== -1 ? i : units.findIndex(u => CASE_TYPES.includes(u.unit_type));
}

function stockSizeOf(units) {
  const stock = units.find(u => u.is_base_stock_unit) || units.find(u => u.is_default_count_unit);
  return parseFloat(stock?.conversion_to_base) || 1;
}

// Count pattern only: one source of truth for items-per-case. Keeps the
// case row size, case_qty and the product case_size equal. No-op otherwise.
export function syncCountCase(f, { items, caseSize } = {}) {
  const units = f.units || [];
  const idx = findCaseIdx(units);
  if (idx === -1 || measureTypeOf(units[idx].base_unit) !== 'count') return f;
  const stockSize = stockSizeOf(units);
  let n = items != null ? parseInt(items, 10) : null;
  if (n == null && caseSize != null) n = parseInt(caseSize, 10);
  if (n == null) {
    const conv = parseFloat(units[idx].conversion_to_base) / stockSize;
    n = Number.isInteger(conv) ? conv : null;
  }
  if (!Number.isInteger(n) || n <= 0) return f;
  return {
    ...f,
    case_size: String(n),
    units: units.map((u, i) => (i === idx
      ? { ...u, _units_per_case: String(n), case_qty: n, conversion_to_base: String(n * stockSize) }
      : u)),
  };
}

export function isCountCaseRow(unit) {
  return !!unit && CASE_TYPES.includes(unit.unit_type) && measureTypeOf(unit.base_unit) === 'count';
}

function CountCaseFields({ caseUnit, onItems }) {
  const items = caseUnit._units_per_case ?? (caseUnit.case_qty != null ? String(caseUnit.case_qty) : '');
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-700">Items per {caseUnit.unit_type} <span className="text-red-500">*</span></label>
        <Input type="number" min="1" step="1" value={items} onChange={(e) => onItems(e.target.value)}
          placeholder="e.g. 24" className="h-8 text-xs" data-testid="case-units-per-case" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-700">Total (auto)</label>
        <div className="h-8 rounded border border-slate-300 bg-white px-2 flex items-center text-xs font-mono" data-testid="case-total-ml">
          {caseUnit.conversion_to_base ? `${caseUnit.conversion_to_base} each` : '—'}
        </div>
      </div>
    </div>
  );
}

export function CaseHelper({ form, setForm }) {
  const units = form.units || [];
  const idx = findCaseIdx(units);
  if (idx === -1) return null;
  const caseUnit = units[idx];
  const base = caseUnit.base_unit || 'ml';
  const mt = measureTypeOf(base);
  const patchCase = (patch, extra = {}) => setForm(f => ({
    ...f, ...extra, units: f.units.map((u, i) => (i === idx ? { ...u, ...patch } : u)),
  }));

  if (mt === 'count') {
    const onItems = (v) => {
      const n = parseInt(v, 10);
      if (Number.isInteger(n) && n > 0) setForm(f => syncCountCase(f, { items: n }));
      else patchCase({ _units_per_case: v });
    };
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-3" data-testid="case-purchase-helper">
        <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Case — items per case (also sets Case / Outer Size)</div>
        <CountCaseFields caseUnit={caseUnit} onItems={onItems} />
      </div>
    );
  }

  const upcVal = caseUnit._units_per_case ?? (caseUnit.case_qty != null ? String(caseUnit.case_qty) : '');
  const indivVal = caseUnit._indiv_ml ?? (mt === 'volume' && caseUnit.unit_size_ml != null ? String(caseUnit.unit_size_ml) : '');
  const computed = parseFloat(upcVal) * parseFloat(indivVal);
  const total = (computed > 0 ? computed : parseFloat(caseUnit.conversion_to_base)) || 0;
  const recalc = (upc, indiv) => {
    const n = parseInt(upc, 10);
    const s = parseFloat(indiv);
    return {
      _units_per_case: upc, _indiv_ml: indiv,
      case_qty: Number.isInteger(n) && n > 0 ? n : caseUnit.case_qty ?? null,
      ...(mt === 'volume' && s > 0 ? { unit_size_ml: s } : {}),
      conversion_to_base: n > 0 && s > 0 ? String(n * s) : caseUnit.conversion_to_base,
    };
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-3" data-testid="case-purchase-helper">
      <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Case / outer unit — fill in to auto-calculate total {base}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">Units per {caseUnit.unit_type} <span className="text-red-500">*</span></label>
          <Input type="number" min="1" step="1" value={upcVal} onChange={(e) => patchCase(recalc(e.target.value, indivVal))}
            placeholder="e.g. 12" className="h-8 text-xs" data-testid="case-units-per-case" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">Individual unit size ({base}) <span className="text-red-500">*</span></label>
          <Input type="number" min="1" step="0.1" value={indivVal} onChange={(e) => patchCase(recalc(upcVal, e.target.value))}
            placeholder="e.g. 700" className="h-8 text-xs" data-testid="case-indiv-ml" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700">Total {base} (auto-calculated)</label>
          <div className={`h-8 rounded border px-2 flex items-center text-xs font-mono ${total ? 'bg-white border-slate-300 text-slate-800' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
            data-testid="case-total-ml">
            {total ? `${total.toLocaleString()} ${base}` : '—'}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-amber-700">These fields fill the "Size" column in the units table. Required for case purchases.</p>
    </div>
  );
}
